import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, ExternalLink, MessageSquareText, CalendarPlus, Check, Pencil } from "lucide-react";
import { toast } from "sonner";
import { toIso, fmtDay } from "./contentUtils";
import { MediaPreview } from "./StatusBadge";
import { toHtml, htmlToText } from "../../lib/richtext";

const has = (v) => (Array.isArray(v) ? v.some((x) => htmlToText(x).trim()) : htmlToText(v || "").trim());
const HL = "[&_[data-cid]]:rounded-sm [&_[data-cid]]:bg-[#F3D2D9] [&_[data-cid]]:px-0.5";

function Block({ label, html, testid }) {
  if (!has(html)) return null;
  const list = Array.isArray(html) ? html.filter((x) => htmlToText(x).trim()) : [html];
  return (
    <div data-testid={testid}>
      <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-ink-muted">{label}</p>
      <div className="mt-2 space-y-2">
        {list.map((h, n) => <div key={n} className={`whitespace-pre-wrap break-words rounded-2xl bg-white px-5 py-4 text-sm font-light leading-relaxed text-ink shadow-[0_2px_12px_rgba(45,24,30,0.05)] ${HL}`} dangerouslySetInnerHTML={{ __html: toHtml(h) }} />)}
      </div>
    </div>
  );
}

function Comments({ list }) {
  if (!list.length) return null;
  return (
    <div data-testid="idea-view-comments" className="rounded-[24px] p-5 text-white" style={{ backgroundColor: "#2D181E", backgroundImage: "radial-gradient(circle at 100% 0%, rgba(123,17,58,0.55), transparent 60%)" }}>
      <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#F3D2D9]"><MessageSquareText size={14} /> Les commentaires de ta coach</p>
      <div className="mt-3 space-y-3">
        {list.map((c) => (
          <div key={c.id} className="rounded-2xl border border-white/10 bg-white/5 p-3">
            {c.quote && <p className="border-l-2 border-[#F3D2D9] pl-2 text-xs italic text-white/60">« {c.quote} »</p>}
            <p className="mt-1.5 whitespace-pre-wrap text-sm font-light text-white/90">{c.text}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function PlanBar({ onPlan }) {
  const [date, setDate] = useState(toIso(new Date()));
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-[22px] bg-[#590224] p-4 text-white">
      <CalendarPlus size={18} className="text-[#F3D2D9]" />
      <span className="text-sm font-medium">Planifier ce contenu</span>
      <input type="date" data-testid="idea-view-plan-date" value={date} onChange={(e) => setDate(e.target.value)} className="ml-auto rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-xs text-white outline-none" />
      <button data-testid="idea-view-plan" onClick={() => onPlan(date)} className="flex items-center gap-1.5 rounded-full bg-white px-4 py-1.5 text-xs font-medium text-[#590224]"><Check size={13} /> Ajouter au calendrier</button>
    </div>
  );
}

export default function CoachIdeaView({ item, actions, onClose, isAdmin, onEdit, mine, insp }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose]);
  const hooks = item.hooks?.length ? item.hooks : item.hook ? [item.hook] : [];
  const comments = (item.comments || []).filter((c) => c.author === "admin");
  const plan = async (date) => { await actions.updateItem(item.id, { date, status: "planifie" }); toast.success(`Ajouté au calendrier le ${fmtDay(date)} ✨`); onClose(); };
  return createPortal(
    <div className="fixed inset-0 z-[100] bg-ink/30 backdrop-blur-[2px]" onClick={onClose}>
      <div data-testid="idea-view" onClick={(e) => e.stopPropagation()} className="fixed bottom-0 right-0 top-0 w-full max-w-2xl overflow-y-auto border-l border-line bg-cream shadow-2xl">
        <div className="space-y-6 p-5 sm:p-8">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-bordeaux">{insp ? (mine ? (isAdmin ? "Inspiration ajoutée par ta cliente" : "Inspiration ajoutée par toi") : "Inspiration suggérée par ta coach") : mine ? (isAdmin ? "Idée de ta cliente" : "Ton idée") : "Idée proposée par ta coach"}</p>
              <h2 data-testid="idea-view-title" className="mt-1 font-sans text-2xl font-medium text-ink">{item.title || "Sans titre"}</h2>
              <div className="mt-2 flex flex-wrap gap-2">
                {item.format && <span className="rounded-full bg-bordeaux px-3 py-1 text-xs font-medium text-white">{item.format}</span>}
                {item.objectif && <span className="rounded-full bg-bordeaux-tint px-3 py-1 text-xs font-medium text-bordeaux">{item.objectif}</span>}
              </div>
            </div>
            <div className="flex items-center gap-1">
              {onEdit && <button data-testid="idea-view-edit" onClick={onEdit} className="flex items-center gap-1.5 rounded-full border border-line bg-white px-3 py-1.5 text-xs font-medium text-bordeaux hover:bg-bordeaux-tint/40"><Pencil size={13} /> Modifier</button>}
              <button data-testid="idea-view-close" onClick={onClose} className="rounded-full p-2 text-ink-muted hover:bg-bordeaux-tint hover:text-bordeaux"><X size={18} /></button>
            </div>
          </div>
          {(item.ref_url || item.image) && (
            <div className="flex items-end gap-4">
              <div className="h-56 w-32 shrink-0 overflow-hidden rounded-2xl border border-line"><MediaPreview url={item.ref_url} image={item.image} tall /></div>
              {item.ref_url && <a data-testid="idea-view-link" href={item.ref_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full border border-line bg-white px-4 py-2 text-sm font-medium text-bordeaux hover:bg-bordeaux-tint/40"><ExternalLink size={14} /> Voir le contenu d'origine</a>}
            </div>
          )}
          <Block testid="idea-view-hooks" label="Accroche" html={hooks} />
          <Block label="Text hook" html={item.text_hooks || []} />
          <Block label="CTA" html={item.cta} />
          <Block label={mine ? (isAdmin ? "Ses notes" : "Tes notes") : insp ? "Les notes de ta coach" : "Le détail de ta coach"} html={item.notes} testid="idea-view-notes" />
          <Block label="Le message à faire passer" html={item.prise_conscience} />
          <Block label="Idées" html={item.idees} />
          <Block label="Script" html={item.post} />
          <Block label="Description" html={item.description} />
          <Comments list={comments} />
          {item.coach_feedback && <Block label="Retour de ta coach" html={item.coach_feedback} />}
          {!isAdmin && !insp && <PlanBar onPlan={plan} />}
        </div>
      </div>
    </div>,
    document.body
  );
}
